import React, { createContext, useState, useEffect } from 'react';
import { auth, db } from '../firebase';
import { doc, onSnapshot, getDoc, updateDoc } from 'firebase/firestore';
import { trace } from '../utils/traceLog';

export const UserContext = createContext({
  username: '',
  photoURL: '',
  completedTrades: 0,
  averageRating: 0,
  totalRatings: 0,
  loading: true,
  setUser: () => {},
  refreshUserData: () => {}
});

export const UserProvider = ({ children }) => {
  const [userData, setUserData] = useState({
    username: '',
    photoURL: '',
    completedTrades: 0,
    averageRating: 0,
    totalRatings: 0,
    loading: true,
    shippingAddress: null,
    firestoreUser: null,
  });

  // Debug: Log only significant userData changes (reduced logging for performance)
  useEffect(() => {
    if (userData.loading === false && userData.username) {
      console.log('🔄 UserContext loaded:', userData.username);
    }
  }, [userData.loading, userData.username]);

  useEffect(() => {
    let unsubscribeSnapshot = null;
    // The uid whose data currently populates state. The cache-miss branch
    // below preserves last-known-good data through transient offline blips,
    // and preserved data must never survive into a DIFFERENT account's
    // session (shared device: sign-out → sign-in, or a direct user switch).
    let subscribedUid = null;

    const unsubscribeAuth = auth.onAuthStateChanged((user) => {
      // Clean up previous snapshot listener
      if (unsubscribeSnapshot) {
        unsubscribeSnapshot();
        unsubscribeSnapshot = null;
      }

      if (!user) {
        console.log('👤 No authenticated user, clearing UserContext');
        subscribedUid = null;
        setUserData({
          username: '',
          photoURL: '',
          completedTrades: 0,
          averageRating: 0,
          totalRatings: 0,
          loading: false,
          shippingAddress: null,
          firestoreUser: null,
        });
        return;
      }

      // Direct account switch with no signed-out state in between: drop the
      // previous user's data immediately instead of showing it until the new
      // user's first snapshot lands.
      if (subscribedUid && subscribedUid !== user.uid) {
        setUserData({
          username: '',
          photoURL: '',
          completedTrades: 0,
          averageRating: 0,
          totalRatings: 0,
          loading: true,
          shippingAddress: null,
          firestoreUser: null,
        });
      }
      subscribedUid = user.uid;

      // Storm dedupe (2026-09-25): the iOS listen storm caught on the offer
      // page delivered one doc every ~8ms. This context feeds most of the
      // app, so re-setting state per identical delivery would re-render the
      // whole tree at storm rate. Skip deliveries whose server version is
      // unchanged (never skip pending-write deliveries).
      let lastUserSnapVersion = null;
      unsubscribeSnapshot = onSnapshot(doc(db, 'users', user.uid), async (docSnap) => {
        const snapVersion = docSnap.updateTime ? docSnap.updateTime.toMillis() : null;
        if (snapVersion !== null && lastUserSnapVersion === snapVersion && !docSnap.metadata.hasPendingWrites) {
          return;
        }
        lastUserSnapVersion = snapVersion;
        if (docSnap.exists()) {
          const data = docSnap.data();
          
          // 🚫 CHECK IF USER IS BANNED - Sign them out immediately
          if (data.accountStatus === 'banned') {
            console.error('🚫 User is banned - signing out');
            alert(`Your account has been banned. Reason: ${data.banReason || 'Violation of terms of service'}`);
            await auth.signOut();
            return; // Stop processing
          }

          // 🛑 AUTO-SUSPENDED pending admin review — sign out immediately.
          // Generic wording on purpose: do not name the matched signal,
          // otherwise legitimate-looking bypasses become easy to iterate.
          if (data.accountStatus === 'suspended_pending_review') {
            console.warn('🛑 Account is suspended pending review - signing out');
            alert('Your account has been suspended pending review. If you believe this is a mistake, please contact contact@pedalxchange.com.');
            await auth.signOut();
            return;
          }
          
          // Auto-update isVerified if BOTH email AND phone are verified AND username exists but Firestore hasn't been updated yet
          if (user.emailVerified && data.phoneVerified && data.username && !data.isVerified) {
            try {
              await updateDoc(doc(db, 'users', user.uid), { 
                isVerified: true,
                emailVerifiedAt: new Date(),
                fullyVerifiedAt: new Date()
              });
              data.isVerified = true;
            } catch (err) {
              console.error('❌ Error updating isVerified:', err);
            }
          }
          
          // Note: isVerified now only tracks email+phone verification, profileComplete tracks Step 4 completion
          // No need to override isVerified based on profile completion status
          
          setUserData({
            username: data.username || '',
            photoURL: data.photoURL || '',
            completedTrades: data.completedTrades || 0,
            averageRating: data.averageRating || 0,
            totalRatings: data.totalRatings || 0,
            loading: false,
            shippingAddress: data.shippingAddress || null,
            firestoreUser: data,
          });
        } else if (docSnap.metadata.fromCache) {
          // Cache-miss while the client is offline/reconnecting (captured in
          // the 2026-09-29 incident log: a transient auth/network blip made
          // this listener deliver exists=false fromCache for a fully
          // registered account, and the whole app briefly believed the
          // profile was gone). An offline cache-miss is UNCONFIRMED — it
          // says "I can't see the doc", not "the doc doesn't exist". Keep
          // the last known-good data (same-uid only; see subscribedUid) and
          // wait for a server-backed delivery to say something real.
          trace('user:cachemiss');
          console.warn(`⚠️ users/${user.uid} cache-miss while offline — keeping last known state`);
          setUserData(prev => ({ ...prev, loading: false }));
        } else {
          // Server-confirmed missing: the backend itself says there is no
          // users/{uid} doc. This is the REAL absent state that signup
          // routing (brand-new account) and account deletion depend on —
          // preserve it exactly.
          console.warn(`⚠️ No Firestore user document found for UID: ${user.uid}`);
          setUserData({
            username: '',
            photoURL: '',
            completedTrades: 0,
            averageRating: 0,
            totalRatings: 0,
            loading: false,
            shippingAddress: null,
            firestoreUser: null,
          });
        }
      }, (error) => {
        // Listener errors (typically permission-denied during an auth
        // transition, or unavailable while offline) keep the last known-good
        // data rather than blanking the app; the listener self-heals or the
        // next auth state rebinds it.
        trace('user:listener:error', { code: error?.code || 'unknown' });
        console.error('❌ UserContext Firestore listener error:', error);
        setUserData(prev => ({
          ...prev,
          loading: false
        }));
      });

      return () => {
        if (unsubscribeSnapshot) {
          unsubscribeSnapshot();
          unsubscribeSnapshot = null;
        }
      };
    });

    return () => {
      if (unsubscribeSnapshot) {
        unsubscribeSnapshot();
      }
      unsubscribeAuth();
    };
  }, []);

  // Manual refresh function
  const refreshUserData = () => {
    const user = auth.currentUser;
    if (user) {
      console.log('🔄 Manual UserContext refresh triggered');
      // Force a re-read of the Firestore document
      const userDocRef = doc(db, 'users', user.uid);
      getDoc(userDocRef).then((docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          console.log('✅ Manual refresh received Firestore data:', data);
          console.log('📸 Manual refresh photoURL:', data.photoURL);
          setUserData({
            username: data.username || '',
            photoURL: data.photoURL || '',
            completedTrades: data.completedTrades || 0,
            averageRating: data.averageRating || 0,
            totalRatings: data.totalRatings || 0,
            loading: false,
            shippingAddress: data.shippingAddress || null,
            firestoreUser: data,
          });
        }
      }).catch(err => {
        console.error('❌ Manual refresh failed:', err);
      });
    }
  };

  // Debug: Log the context value being provided
  const contextValue = { ...userData, setUser: setUserData, refreshUserData };
  console.log('🎯 UserContext providing value:', {
    username: contextValue.username,
    loading: contextValue.loading,
    shippingAddress: contextValue.shippingAddress,
    hasFirestoreUser: !!contextValue.firestoreUser
  });

  return (
    <UserContext.Provider value={contextValue}>
      {children}
    </UserContext.Provider>
  );
};
